(() => {
  if (window.ToneCart) return;
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const findFocus = (root, key) => Array.from(root.querySelectorAll('[data-focus-key]')).find((element) => element.dataset.focusKey === key);
  const cart = {
    busy: false,
    refreshing: false,
    pendingRefresh: false,
    stale: false,
    editorOpenSection: null,
    edits: new Map(),
    editFailure: false,
    draining: false,
    pendingSubmit: null,
    get drawer() { return document.querySelector('tone-cart-drawer'); },
    get config() {
      try { return JSON.parse(this.drawer?.dataset.cartConfig || 'null'); } catch { return null; }
    },
    sectionIds() {
      const sections = [this.drawer?.dataset.cartSection, ...Array.from(document.querySelectorAll('[data-cart-page]'), (element) => element.dataset.cartSection)].filter(Boolean);
      document.dispatchEvent(new CustomEvent('tone:cart-sections', { detail: { sections } }));
      document.querySelectorAll('[data-cart-refresh-section]').forEach((element) => sections.push(element.dataset.cartRefreshSection));
      return Array.from(new Set(sections.filter((section) => typeof section === 'string' && section.length < 200))).slice(0, 5);
    },
    contextURL() {
      const url = new URL(window.location.href);
      ['sections', 'section_id'].forEach((key) => url.searchParams.delete(key));
      return url.pathname + url.search;
    },
    lock(locked) {
      this.busy = locked;
      document.documentElement.classList.toggle('tone-cart-busy', locked);
      if (locked) {
        this.lockedControls ||= [];
        const tracked = new Set(this.lockedControls.map(({ element }) => element));
        document.querySelectorAll('[data-product-form] button[type="submit"], [data-cart-form] button, [data-cart-form] input, [data-cart-form] textarea, [data-cart-discount-form] button, [data-cart-discount-form] input, [form^="DiscountForm-"]').forEach((element) => {
          if (element.matches('[data-cart-quantity], [name="note"], [name="checkout"]')) return;
          if (!tracked.has(element)) this.lockedControls.push({ element, disabled: element.disabled });
          element.disabled = true;
        });
      } else {
        this.lockedControls?.forEach(({ element, disabled }) => { if (element.isConnected) element.disabled = disabled; });
        this.lockedControls = [];
        queueMicrotask(() => this.drainEdits());
      }
    },
    quantityInputs(root = document) {
      return Array.from(root.querySelectorAll('[data-cart-quantity]'));
    },
    editInput(edit) {
      const form = document.getElementById(edit.formId);
      return [...(form ? this.quantityInputs(form) : []), ...this.quantityInputs()].find((input) => input.dataset.lineKey === edit.key && input.dataset.lineIdentity === edit.identity);
    },
    pauseEdits() {
      this.editFailure = this.edits.size > 0;
      this.pendingSubmit = null;
      this.edits.forEach((edit) => { clearTimeout(edit.timer); edit.ready = false; });
    },
    queueQuantity(input, immediate = false, remove = false) {
      if (!this.config || !window.fetch || this.replacingView || this.reportingFeedback || !input.isConnected) return;
      const form = input.closest('[data-cart-form]');
      if (this.stale) { this.feedback(form, this.config.uncertain, true); return; }
      const key = input.dataset.lineKey;
      const value = remove ? '0' : input.value;
      let edit = this.edits.get(key);
      if (!edit && !remove && value === input.defaultValue) return;
      if (!edit) {
        edit = { key, identity: input.dataset.lineIdentity, revision: 0, formId: form.id };
        this.edits.set(key, edit);
      }
      if (edit.value !== value || edit.remove !== remove) edit.revision += 1;
      Object.assign(edit, { value, remove, formId: form.id });
      clearTimeout(edit.timer);
      this.editFailure = false;
      edit.ready = false;
      if (!remove) this.quantityInputs().filter((field) => field.dataset.lineKey === key).forEach((field) => { field.value = value; });
      const valid = remove || input.validity.valid;
      if (!valid) {
        this.pendingSubmit = null;
        if (immediate) input.reportValidity();
        return;
      }
      if (immediate || this.pendingSubmit) {
        edit.ready = true;
        this.drainEdits();
      } else {
        edit.timer = setTimeout(() => { edit.ready = true; this.drainEdits(); }, 350);
      }
    },
    reconcileEdits(root) {
      const inputs = this.quantityInputs(root);
      const next = new Map();
      for (const edit of this.edits.values()) {
        const exact = inputs.find((input) => input.dataset.lineKey === edit.key);
        const matching = inputs.filter((input) => input.dataset.lineIdentity === edit.identity);
        const previousKeys = new Set(this.quantityInputs().filter((field) => field.dataset.lineIdentity === edit.identity).map((field) => field.dataset.lineKey));
        const uniqueBefore = previousKeys.size === 1 && previousKeys.has(edit.key);
        const input = exact?.dataset.lineIdentity === edit.identity ? exact : !exact && uniqueBefore && matching.length === 1 ? matching[0] : null;
        if (!input) {
          if (edit.remove && !exact && !matching.length) { clearTimeout(edit.timer); continue; }
          this.pauseEdits();
          return false;
        }
        edit.previousKey = edit.key;
        edit.key = input.dataset.lineKey;
        if (next.has(edit.key)) { this.pauseEdits(); return false; }
        next.set(edit.key, edit);
      }
      this.edits = next;
      return true;
    },
    async drainEdits() {
      if (this.draining || this.busy || this.refreshing || this.stale || this.editFailure) return;
      this.draining = true;
      try {
        while (!this.busy && !this.refreshing && !this.stale && !this.editFailure) {
          const edit = Array.from(this.edits.values()).find((entry) => entry.ready);
          if (!edit) break;
          const input = this.editInput(edit);
          if (!input) { this.pauseEdits(); this.markStale(); break; }
          if (!edit.remove && !input.validity.valid) { edit.ready = false; this.pendingSubmit = null; continue; }
          const revision = edit.revision;
          const form = document.getElementById(edit.formId);
          const written = await this.write('change', { id: edit.key, quantity: Number(edit.value) }, form, false, { edit, revision });
          if (!written) break;
        }
      } finally {
        this.draining = false;
        this.resumeSubmit();
      }
    },
    deferSubmit(form, submitter) {
      if (this.editFailure) { this.feedback(form, this.config.uncertain, true); return; }
      this.pendingSubmit = { formId: form.id, name: submitter?.name };
      for (const edit of this.edits.values()) {
        clearTimeout(edit.timer);
        const input = this.editInput(edit);
        if (!input || (!edit.remove && !input.reportValidity())) { this.pendingSubmit = null; return; }
        edit.ready = true;
      }
      this.drainEdits();
    },
    resumeSubmit() {
      if (!this.pendingSubmit || this.busy || this.refreshing || this.edits.size || this.stale || this.editFailure) return;
      const { formId, name } = this.pendingSubmit;
      this.pendingSubmit = null;
      const form = document.getElementById(formId);
      const button = Array.from(form?.querySelectorAll('[type="submit"]') || []).find((element) => element.name === name);
      if (form?.reportValidity()) form.requestSubmit(button);
    },
    feedback(form, message, recovery = false) {
      const connectedForm = form?.isConnected ? form : document.getElementById(form?.getAttribute('id') || '');
      const root = connectedForm?.matches('[data-product-form]') ? connectedForm : connectedForm?.closest('tone-cart-view') || this.drawer?.querySelector('tone-cart-view');
      const output = root?.querySelector('[data-product-feedback], [data-cart-feedback]');
      if (output) {
        output.textContent = message;
        output.hidden = !message;
        if (message) {
          this.reportingFeedback = true;
          try { output.focus({ preventScroll: false }); } finally { this.reportingFeedback = false; }
        }
      }
      const link = root?.querySelector('[data-product-recovery], [data-cart-recovery]');
      if (link) link.hidden = !recovery;
    },
    preserveDrafts(root) {
      return Array.from(root.querySelectorAll('[name="note"], [name="discount"]')).filter((input) => input.value !== input.defaultValue).map((input) => ({ name: input.name, value: input.value }));
    },
    replaceView(current, next) {
      const content = current?.querySelector('[data-cart-view-content]');
      const nextContent = next?.querySelector('[data-cart-view-content]');
      if (!content || !nextContent) return;
      const focused = current.contains(document.activeElement);
      const key = focused ? document.activeElement.dataset.focusKey : null;
      const drafts = this.preserveDrafts(current);
      const focusedLineKey = focused ? document.activeElement.dataset.lineKey || document.activeElement.dataset.cartRemove : null;
      const focusedInput = focusedLineKey ? this.quantityInputs(current).find((input) => input.dataset.lineKey === focusedLineKey) : null;
      const focusedIdentity = focusedInput?.dataset.lineIdentity;
      const previousFocusKeys = new Set(this.quantityInputs(current).filter((input) => input.dataset.lineIdentity === focusedIdentity).map((input) => input.dataset.lineKey));
      const nextFocusInputs = focusedIdentity ? this.quantityInputs(nextContent).filter((input) => input.dataset.lineIdentity === focusedIdentity) : [];
      const resolvedFocusKey = nextFocusInputs.find((input) => input.dataset.lineKey === focusedLineKey)?.dataset.lineKey || (previousFocusKeys.size === 1 && nextFocusInputs.length === 1 ? nextFocusInputs[0].dataset.lineKey : null);
      this.preserveAppBlocks(content, nextContent);
      nextContent.querySelectorAll('noscript').forEach((element) => element.remove());
      this.replacingView = true;
      try { content.replaceChildren(...Array.from(nextContent.childNodes)); } finally { this.replacingView = false; }
      drafts.forEach(({ name, value }) => {
        const input = current.querySelector(`[name="${name}"]`);
        if (input) input.value = value;
      });
      this.edits.forEach((edit) => {
        if (!edit.remove) this.quantityInputs(current).filter((input) => input.dataset.lineKey === edit.key).forEach((input) => { input.value = edit.value; });
      });
      const nextKey = resolvedFocusKey ? `${key?.startsWith('remove-') ? 'remove' : 'quantity'}-${resolvedFocusKey}` : key;
      if (focused) (findFocus(current, nextKey) || current.querySelector('[data-cart-heading]'))?.focus({ preventScroll: true });
    },
    preserveAppBlocks(current, next) {
      const blocks = new Map(Array.from(current.querySelectorAll('[data-commerce-app-block]'), (block) => [block.dataset.commerceAppBlock, block]));
      next.querySelectorAll('[data-commerce-app-block]').forEach((block) => {
        const existing = blocks.get(block.dataset.commerceAppBlock);
        if (existing) block.replaceWith(existing);
      });
    },
    markStale() {
      this.stale = true;
      document.querySelectorAll('tone-cart-view').forEach((view) => {
        const output = view.querySelector('[data-cart-feedback]');
        if (output) { output.textContent = this.config?.uncertain || ''; output.hidden = false; }
        const link = view.querySelector('[data-cart-recovery]');
        if (link) link.hidden = false;
      });
    },
    applySections(sections, ids) {
      if (!sections || typeof sections !== 'object') return false;
      const documents = new Map();
      for (const id of ids) {
        if (typeof sections[id] !== 'string' || !sections[id].trim()) continue;
        documents.set(id, parseHTML(sections[id]));
      }
      const drawer = this.drawer;
      const nextDrawer = documents.get(drawer?.dataset.cartSection)?.querySelector('tone-cart-drawer');
      if (drawer && (!nextDrawer?.querySelector('[data-cart-view-content]') || !nextDrawer?.querySelector('[data-cart-server-state]') || !nextDrawer?.querySelector('[data-shipping-template]'))) return false;
      const pageViews = Array.from(document.querySelectorAll('[data-cart-page]'));
      if (pageViews.some((view) => !documents.get(view.dataset.cartSection)?.querySelector('[data-cart-page] [data-cart-view-content]'))) return false;
      const editsResolved = this.reconcileEdits(nextDrawer || documents.get(pageViews[0]?.dataset.cartSection) || document);
      if (drawer && nextDrawer) {
        this.replaceView(drawer.querySelector('tone-cart-view'), nextDrawer.querySelector('tone-cart-view'));
        drawer.dataset.cartConfig = nextDrawer.dataset.cartConfig;
        drawer.querySelector('[data-cart-server-state]').replaceWith(nextDrawer.querySelector('[data-cart-server-state]'));
        drawer.querySelector('[data-shipping-template]').replaceWith(nextDrawer.querySelector('[data-shipping-template]'));
      }
      pageViews.forEach((view) => this.replaceView(view, documents.get(view.dataset.cartSection).querySelector('[data-cart-page]')));
      documents.forEach((documentFragment, id) => {
        if (id === drawer?.dataset.cartSection || pageViews.some((view) => view.dataset.cartSection === id)) return;
        const current = document.getElementById(`shopify-section-${id}`);
        const next = documentFragment.getElementById(`shopify-section-${id}`);
        if (current && next) {
          this.preserveAppBlocks(current, next);
          current.replaceChildren(...Array.from(next.childNodes));
        }
      });
      this.stale = false;
      if (!editsResolved) this.markStale();
      if (this.busy) this.lock(true);
      this.syncServerState();
      document.dispatchEvent(new CustomEvent('tone:sections-rendered', { detail: { sections: ids } }));
      return true;
    },
    syncServerState() {
      const state = this.drawer?.querySelector('[data-cart-server-state]');
      if (!state) return;
      document.querySelectorAll('[data-cart-count]').forEach((element) => { element.textContent = state.dataset.count; });
      document.querySelectorAll('[data-cart-open]').forEach((element) => { element.setAttribute('aria-label', state.dataset.countLabel); });
      const shipping = this.drawer.querySelector('[data-shipping-template]')?.content.querySelector('[data-tone-shipping]');
      if (!shipping) return;
      document.querySelectorAll('[data-tone-shipping]').forEach((target) => {
        const copy = shipping.cloneNode(true);
        const ids = new Map();
        copy.querySelectorAll('[id]').forEach((element) => {
          const original = element.id;
          element.id = `${target.id}-${original}`;
          ids.set(original, element.id);
        });
        copy.querySelectorAll('[aria-labelledby], [aria-describedby], [for]').forEach((element) => {
          ['aria-labelledby', 'aria-describedby', 'for'].forEach((attribute) => {
            if (element.hasAttribute(attribute)) element.setAttribute(attribute, element.getAttribute(attribute).split(' ').map((value) => ids.get(value) || value).join(' '));
          });
        });
        target.replaceChildren(...Array.from(copy.childNodes));
        target.hidden = shipping.hidden;
      });
    },
    async refresh() {
      if (!this.config || !window.fetch) return false;
      if (this.refreshing) { this.pendingRefresh = true; return false; }
      this.refreshing = true;
      const ownsLock = !this.busy;
      if (ownsLock) {
        this.lock(true);
        const live = this.drawer?.querySelector('[data-cart-live]');
        if (live) live.textContent = this.config.refreshing;
      }
      const request = new AbortController();
      const timeout = setTimeout(() => request.abort(), 15000);
      try {
        const ids = this.sectionIds();
        const url = new URL(this.contextURL(), window.location.origin);
        url.searchParams.set('sections', ids.join(','));
        const response = await fetch(url, { headers: { Accept: '*/*' }, signal: request.signal, cache: 'no-store' });
        if (!response.ok || !/^(application\/json|text\/javascript)\b/i.test(response.headers.get('content-type') || '')) throw new Error('render');
        const sections = await response.json();
        const rendered = this.applySections(sections, ids);
        if (!rendered) this.markStale();
        return rendered;
      } catch (error) {
        this.markStale();
        throw error;
      } finally {
        clearTimeout(timeout);
        this.refreshing = false;
        if (ownsLock) {
          this.lock(false);
          const live = this.drawer?.querySelector('[data-cart-live]');
          if (live) live.textContent = this.stale ? this.config.uncertain : this.config.updated;
        }
        if (this.pendingRefresh && !this.busy) {
          this.pendingRefresh = false;
          this.refresh().catch(() => {});
        }
      }
    },
    async write(endpoint, body, form, adding = false, transaction = null) {
      const config = this.config;
      if (!config || this.busy || this.refreshing) return false;
      if (this.stale) { window.location.assign(config.cart); return false; }
      if (!navigator.onLine) { this.pauseEdits(); this.feedback(form, config.offline, true); return false; }
      const ids = this.sectionIds();
      const url = config[endpoint];
      if (!url) return;
      if (body instanceof FormData) {
        body.set('sections', ids.join(','));
        body.set('sections_url', this.contextURL());
      } else {
        body.sections = ids;
        body.sections_url = this.contextURL();
      }
      this.feedback(form, '');
      this.lock(true);
      form?.setAttribute('aria-busy', 'true');
      const request = new AbortController();
      const timeout = setTimeout(() => request.abort(), 25000);
      let confirmed = false;
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: body instanceof FormData ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: body instanceof FormData ? body : JSON.stringify(body),
          signal: request.signal,
        });
        const isJSON = /^(application\/json|text\/javascript)\b/i.test(response.headers.get('content-type') || '');
        if (!isJSON) throw Object.assign(new Error(config.uncertain), { uncertain: true });
        const result = await response.json();
        if (!response.ok || result.status >= 400) {
          const message = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : config.error;
          throw Object.assign(new Error(message.slice(0, 600)), { known: response.status < 500, uncertain: response.status >= 500 });
        }
        confirmed = true;
        if (transaction && transaction.edit.revision === transaction.revision) this.edits.delete(transaction.edit.key);
        const submittedNote = body instanceof FormData ? body.get('note') : body.note;
        const note = form?.querySelector('[name="note"]');
        if (typeof submittedNote === 'string' && note?.value === submittedNote) note.defaultValue = submittedNote;
        clearTimeout(timeout);
        let rendered = this.applySections(result.sections, ids);
        if (!rendered) rendered = await this.refresh().catch(() => false);
        if (!rendered) {
          window.location.assign(config.cart);
          return;
        }
        if (adding && this.drawer?.dataset.cartMode === 'page') {
          window.location.assign(config.cart);
          return;
        }
        if (adding && this.drawer?.dataset.autoOpen === 'true') this.drawer.open(form?.querySelector('[type="submit"]'));
        const live = this.drawer?.querySelector('[data-cart-live]');
        if (live) live.textContent = adding ? config.added : config.updated;
        if (adding && this.drawer?.dataset.autoOpen !== 'true') this.feedback(form, config.added, true);
        const detail = { source: 'tone', sections: ids };
        if (typeof result.item_count === 'number') detail.cart = result;
        document.dispatchEvent(new CustomEvent('tone:cart-updated', { detail }));
        return true;
      } catch (error) {
        this.pauseEdits();
        if (confirmed) {
          window.location.assign(config.cart);
          return;
        }
        const reconciled = navigator.onLine && await this.refresh().catch(() => false);
        if (!reconciled) this.markStale();
        const uncertain = !error.known || !reconciled;
        this.feedback(form, uncertain ? config.uncertain : error.message || config.error, uncertain);
        document.dispatchEvent(new CustomEvent('tone:cart-error', { detail: { message: uncertain ? config.uncertain : error.message || config.error, uncertain, stale: this.stale } }));
        return false;
      } finally {
        clearTimeout(timeout);
        form?.removeAttribute('aria-busy');
        this.lock(false);
        if (this.pendingRefresh) {
          this.pendingRefresh = false;
          this.refresh().catch(() => {});
        }
      }
    },
  };
  window.ToneCart = cart;
  if (!customElements.get('tone-cart-view')) {
    class ToneCartView extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        this.addEventListener('submit', (event) => {
          const form = event.target;
          if (!form.matches('[data-cart-form], [data-cart-discount-form]') || !cart.config || !window.fetch) return;
          if (cart.stale) { event.preventDefault(); window.location.assign(cart.config.cart); return; }
          if (form.matches('[data-cart-discount-form]') || event.submitter?.name === 'checkout') {
            if (cart.busy || cart.refreshing || cart.edits.size || cart.editFailure) {
              event.preventDefault();
              cart.deferSubmit(form, event.submitter);
            }
            return;
          }
          event.preventDefault();
          if (!form.reportValidity()) return;
          cart.quantityInputs(form).forEach((input) => cart.queueQuantity(input, true));
        }, { signal: this.controller.signal });
        this.addEventListener('click', (event) => {
          const link = event.target.closest('[data-cart-remove]');
          if (!link || !this.contains(link) || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !cart.config || !window.fetch) return;
          event.preventDefault();
          const input = cart.quantityInputs(this).find((field) => field.dataset.lineKey === link.dataset.cartRemove);
          if (input) cart.queueQuantity(input, true, true);
        }, { signal: this.controller.signal });
        ['input', 'change', 'focusout'].forEach((type) => {
          this.addEventListener(type, (event) => {
            if (event.target.matches('[data-cart-quantity]')) cart.queueQuantity(event.target, type !== 'input');
          }, { signal: this.controller.signal });
        });
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' || event.isComposing || !event.target.matches('[data-cart-quantity]') || !cart.config || !window.fetch) return;
          event.preventDefault();
          cart.queueQuantity(event.target, true);
        }, { signal: this.controller.signal });
      }
      disconnectedCallback() { this.controller?.abort(); }
    }
    customElements.define('tone-cart-view', ToneCartView);
  }
  if (!customElements.get('tone-cart-drawer')) {
    class ToneCartDrawer extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        this.dialog = this.querySelector('dialog');
        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-cart-close]')) this.dialog.close();
          if (event.target === this.dialog) {
            const rect = this.dialog.getBoundingClientRect();
            if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
          }
        }, { signal: this.controller.signal });
        this.dialog.addEventListener('close', () => {
          if (window.Shopify?.designMode) cart.editorOpenSection = null;
          document.documentElement.classList.remove('tone-cart-open');
          const opener = this.opener?.isConnected ? this.opener : document.querySelector('[data-cart-open]');
          opener?.focus({ preventScroll: true });
        }, { signal: this.controller.signal });
        cart.syncServerState();
        if (window.Shopify?.designMode && cart.editorOpenSection === this.dataset.cartSection) this.open();
      }
      disconnectedCallback() {
        this.controller?.abort();
        if (window.Shopify?.designMode && this.dialog?.open) cart.editorOpenSection = this.dataset.cartSection;
        if (this.dialog?.open) this.dialog.close();
        document.documentElement.classList.remove('tone-cart-open');
      }
      open(opener) {
        if (cart.stale && !window.Shopify?.designMode) { window.location.assign(cart.config.cart); return; }
        if (!this.dialog?.showModal || this.dialog.open) return;
        this.opener = opener || document.activeElement;
        this.dialog.showModal();
        document.documentElement.classList.add('tone-cart-open');
        this.querySelector('[data-cart-close]')?.focus();
      }
    }
    customElements.define('tone-cart-drawer', ToneCartDrawer);
  }
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form.matches('[data-product-form]') || event.defaultPrevented || !cart.config || !window.fetch) return;
    event.preventDefault();
    if (cart.busy || cart.refreshing || form.closest('tone-product')?.getAttribute('aria-busy') === 'true' || !form.reportValidity()) return;
    const button = event.submitter || form.querySelector('[type="submit"]');
    if (button?.disabled || !form.querySelector('[name="id"]')?.value) return;
    cart.write('add', new FormData(form), form, true);
  });
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-cart-open]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || !cart.drawer?.dialog?.showModal || cart.drawer.dataset.cartMode === 'page') return;
    event.preventDefault();
    cart.drawer.open(link);
  });
  document.addEventListener('tone:cart-updated', (event) => {
    if (event.detail?.source === 'tone') return;
    if (cart.busy || cart.refreshing) { cart.pendingRefresh = true; return; }
    cart.refresh().catch(() => {});
  });
  ['shopify:section:select', 'shopify:block:select', 'shopify:section:load'].forEach((type) => {
    document.addEventListener(type, (event) => {
      if (!window.Shopify?.designMode) return;
      const drawer = cart.drawer;
      if (!drawer) return;
      const matches = event.detail?.sectionId === drawer.dataset.cartSection || event.target === drawer || drawer.contains(event.target) || event.target.contains?.(drawer);
      if (!matches || (type === 'shopify:section:load' && cart.editorOpenSection !== drawer.dataset.cartSection)) return;
      cart.editorOpenSection = drawer.dataset.cartSection;
      drawer.open();
    });
  });
  document.addEventListener('shopify:section:deselect', (event) => {
    if (window.Shopify?.designMode && event.detail?.sectionId === cart.editorOpenSection) {
      cart.editorOpenSection = null;
      cart.drawer?.dialog?.close();
    }
  });
  window.addEventListener('pageshow', (event) => { if (event.persisted && !cart.busy) cart.refresh().catch(() => {}); });
})();
